import { findSingleGameData } from "../nonUIFuncs";

/**
 * Solve It needs a card for every number from 0 to SOLVE_IT_MAX: it builds
 * equations out of them (up to 20 on Hard) and checks the answer against each
 * card's English value. One missing number deals a broken round, so the game
 * is only offered when every number is there.
 */
export const SOLVE_IT_MAX = 20;

const NUMBER_WORDS = [
  "zero", "one", "two", "three", "four", "five", "six", "seven", "eight",
  "nine", "ten", "eleven", "twelve", "thirteen", "fourteen", "fifteen",
  "sixteen", "seventeen", "eighteen", "nineteen", "twenty",
];

/**
 * A row's English value as a number: digits ("7") or a spelled-out number
 * ("seven", any case). Anything else, or a number above SOLVE_IT_MAX, is null.
 *
 * @param {*} english - A vocab row's English value
 * @returns {number|null}
 */
export const parseSolveItNumber = (english) => {
  const text = String(english ?? "").trim().toLowerCase();
  const n = /^\d+$/.test(text) ? Number(text) : NUMBER_WORDS.indexOf(text);
  return n >= 0 && n <= SOLVE_IT_MAX ? n : null;
};

/**
 * Checks a unit's vocab rows for Solve It.
 *
 * Every number 0..SOLVE_IT_MAX must appear exactly once, as its English value
 * ("7" or "seven"), with a Language word to show on the card. Other rows in
 * the unit are ignored. The game does its math on English, so each returned
 * row has English converted to digits ("seven" -> "7").
 *
 * @param {Array} rows - The unit's vocab rows
 * @returns {{numbers: Array|null, problems: Array<string>}} `numbers[n]` is
 *   the row for n, or null (with the reasons in `problems`) if any is missing
 */
export const findSolveItNumbers = (rows = []) => {
  const byNumber = new Map();
  const problems = [];
  rows.forEach((row) => {
    const n = parseSolveItNumber(row?.English);
    if (n === null) return;
    if (byNumber.has(n)) {
      problems.push(`${n} appears more than once`);
      return;
    }
    byNumber.set(n, { ...row, English: String(n) });
  });
  const numbers = [];
  for (let n = 0; n <= SOLVE_IT_MAX; n++) {
    const row = byNumber.get(n);
    if (!row) {
      problems.push(`${n} is missing`);
    } else if (!String(row.Language ?? "").trim()) {
      problems.push(`${n} has no Language word`);
    }
    numbers.push(row);
  }
  return problems.length ? { numbers: null, problems } : { numbers, problems };
};

/**
 * The rows Solve It would get for a unit: the same vocab and dialect pick
 * FullGameContainer uses, but before its rank filter, which can drop numbers
 * at low levels (numbers don't get harder by rank).
 *
 * @param {Object} storeValue - `store.getState().store.value`
 * @param {string} gameTarget - The unit's game name (`game-name` param / `target`)
 * @param {string} [locationParam] - The `location` param, for dialect communities
 * @param {Array} [dialectStructure] - `config.dialectStructure`
 */
export const solveItRowsFor = (
  storeValue,
  gameTarget,
  locationParam,
  dialectStructure,
) => {
  const vocab =
    locationParam === "Minto" ? storeValue.b_vocabData : storeValue.vocabData;
  return findSingleGameData(
    gameTarget,
    vocab || [],
    locationParam,
    dialectStructure,
  );
};
